import api from '../api';
import { User } from '../types/auth';
import { QuizAttempt } from '../types/quiz';

export interface DashboardStats {
    total_users: number;
    active_subscriptions: number;
    quiz_attempts: {
        daily: number;
        monthly: number;
        daily_guest: number;
        monthly_guest: number;
    };
}

export interface SubscriptionPlan {
    plan_id: number;
    name: string;
    description?: string;
    duration_months: number;
    price: number;
    features: Record<string, any>;
    is_active: boolean;
}

export interface AdminLog {
    log_id: number;
    admin_id: number;
    action: string;
    entity_type: string;
    entity_id?: number;
    details?: Record<string, any>;
    created_at: string;
}

export class AdminService {
    // Dashboard
    static async getDashboardStats(token: string): Promise<DashboardStats> {
        const response = await api.get('/admin/dashboard/stats', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    }

    // Content Creator Management
    static async getContentCreators(token: string): Promise<User[]> {
        const response = await api.get('/admin/users/content-creators', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    }

    static async createContentCreator(token: string, data: {
        email: string;
        password: string;
        first_name: string;
        last_name: string;
        phone: string;
    }): Promise<User> {
        const response = await api.post('/admin/users/content-creators', data, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    }

    // Subscription Plan Management
    static async getSubscriptionPlans(token: string): Promise<SubscriptionPlan[]> {
        const response = await api.get('/admin/subscriptions/plans', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    }

    static async createSubscriptionPlan(token: string, data: Partial<SubscriptionPlan>): Promise<SubscriptionPlan> {
        const response = await api.post('/admin/subscriptions/plans', data, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    }

    // Reports
    static async getQuizParticipationReport(token: string, params?: {
        start_date?: string;
        end_date?: string;
        user_type?: 'all' | 'registered' | 'guest';
    }): Promise<QuizAttempt[]> {
        const response = await api.get('/admin/reports/quiz-participation', {
            params,
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    }

    static async getSubscriptionStatusReport(token: string): Promise<{
        active_subscriptions: any[];
        recently_expired: any[];
    }> {
        const response = await api.get('/admin/reports/subscription-status', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    }

    static async getAuditLogs(token: string, params?: {
        start_date?: string;
        end_date?: string;
        action?: string;
        entity_type?: string;
    }): Promise<AdminLog[]> {
        const response = await api.get('/admin/audit-logs', {
            params,
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    }

    // Email Certificate Settings
    static async getEmailCertificateEnabled(token: string): Promise<boolean> {
        const response = await api.get('/admin/settings/email-certificate-enabled', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data.value === 'true';
    }

    static async updateEmailCertificateEnabled(token: string, enabled: boolean): Promise<void> {
        await api.put('/admin/settings/email-certificate-enabled', 
            { value: enabled.toString() },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );
    }

    static async getMaxFreeCertificates(token: string): Promise<number> {
        const response = await api.get('/admin/settings/max-free-certificates', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data.value;
    }

    static async updateMaxFreeCertificates(token: string, value: number): Promise<void> {
        const response = await api.put('/admin/settings/max-free-certificates', 
            { value },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );
        return response.data;
    }

    // Batch Report Settings
    static async getBatchReportTime(token: string): Promise<string> {
        const response = await api.get('/admin/settings/batch-report-time', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data.value;
    }

    static async updateBatchReportTime(token: string, time: string): Promise<void> {
        await api.put('/admin/settings/batch-report-time', 
            { value: time },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );
    }

    static async getBatchReportEmails(token: string): Promise<string[]> {
        const response = await api.get('/admin/settings/batch-report-emails', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data.value ? JSON.parse(response.data.value) : [];
    }

    static async updateBatchReportEmails(token: string, emails: string[]): Promise<void> {
        await api.put('/admin/settings/batch-report-emails', 
            { value: JSON.stringify(emails) },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );
    }
    
    // Trigger unverified students batch report
    static async triggerUnverifiedStudentsBatchReport(token: string): Promise<{ success: boolean; message: string }> {
        const response = await api.post('/admin/batch-report/unverified-students', {}, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    }

    // Name Fields Editing Settings
    static async getAllowNameFieldsEditing(token: string): Promise<boolean> {
        const response = await api.get('/admin/settings/allow-name-fields-editing', {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data.value === 'true';
    }

    static async updateAllowNameFieldsEditing(token: string, enabled: boolean): Promise<void> {
        await api.put('/admin/settings/allow-name-fields-editing', 
            { value: enabled.toString() },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );
    }
}
